// AiCredits.js — Reports > AI Credit Usage (SS-4282)
//
// The AI credits a location used in a date range, split by what used them: AI-answered
// calls, AI text answers, call translations and Office Optimizer (which includes the
// patient insights and chair-side questions). One location (?bid=) shows its own row; with
// no bid the shared Locations picker (ReportDownloadButton.js) scopes the report and the
// page shows one row per location with a Location column and a totals row summed here.
// Same date control and default (Today) as AI Revenue. Download CSV is the api's array CSV.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCoins,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cqAPI } from "../../lib/Auth";

//api row field -> column, in order
export const CREDIT_COLUMNS = [
  { key: "credits_call", label: "Calls" },
  { key: "credits_text", label: "Texts" },
  { key: "credits_translation", label: "Translations" },
  { key: "credits_office_optimizer", label: "Office Optimizer" },
  { key: "credits_total", label: "Total" },
];

export const sumCredits = (rows) => CREDIT_COLUMNS.reduce((acc, { key }) => ({
  ...acc,
  [key]: (rows || []).reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
}), {});

const formatCredits = (value) => (Number(value) || 0).toLocaleString("en-US");

const rangeForPreset = (value) => {
  switch (value) {
    case "Today":
      return [moment().startOf("day"), moment().endOf("day")];
    case "Yesterday":
      return [moment().subtract(1, "days").startOf("day"), moment().subtract(1, "days").endOf("day")];
    case "Last 7 days":
      return [moment().subtract(6, "days").startOf("day"), moment().endOf("day")];
    case "Last 4 weeks":
      return [moment().subtract(4, "weeks").startOf("day"), moment().endOf("day")];
    case "Month to Date":
      return [moment().startOf("month"), moment().endOf("day")];
    case "Quarter to date":
      return [moment().startOf("quarter"), moment().endOf("day")];
    case "Year to date":
      return [moment().startOf("year"), moment().endOf("day")];
    case "Custom":
    default:
      return [null, null];
  }
};

function AiCredits() {
  const location = useLocation();
  //no bid: the report covers the locations picked in the Locations picker, one row each
  const isMultiLocation = !new URLSearchParams(location.search).get("bid");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  //only the newest request may fill the page: a slower answer for an earlier range must not
  //overwrite the one on screen
  const latestRequest = useRef(0);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const { colorMode } = useColorMode();

  const fetchCredits = async (sd, ed) => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    const isCurrent = () => requestId === latestRequest.current;
    setIsLoading(true);
    setLoadError(false);
    try {
      const response = await cqAPI.get(
        `/reports/ai-credits?rangeBeg=${moment(sd).format("L")}&rangeEnd=${moment(ed).format("L")}`,
        {},
        0
      );
      if (!isCurrent()) {
        return;
      }
      if (response?.type === "ok") {
        setRows(response.data || []);
      } else {
        //never leave the previous range's figures under the newly chosen dates
        setRows([]);
        setLoadError(true);
      }
    } catch (error) {
      if (!isCurrent()) {
        return;
      }
      console.error("Error fetching AI credit usage:", error);
      setRows([]);
      setLoadError(true);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    //on mount: the range from the URL hash (shareable reports), else Today
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const sd = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const ed = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).endOf("day").toDate()
      : moment().endOf("day").toDate();
    setDateRange([sd, ed]);
    setSelectedRange(hashParams.get("selectedRange") || "Today");
    fetchCredits(sd, ed);
  }, []);

  useEffect(() => {
    //keep the URL up to date for shareable links (and the CSV, which reads the hash)
    window.location.hash = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
  }, [startDate, endDate, selectedRange]);

  const sortedRows = useMemo(() => (isMultiLocation
    ? [...rows].sort((a, b) => String(a.location_name || "").localeCompare(String(b.location_name || "")))
    : rows), [rows, isMultiLocation]);
  const totals = useMemo(() => sumCredits(rows), [rows]);

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    const [s, e] = rangeForPreset(value);
    setDateRange([s ? s.toDate() : null, e ? e.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>{date.toLocaleString("default", { month: "long", year: "numeric" })}</Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%" position="relative">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex mb={4} align="center">
        <Select
          aria-label="Date range"
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          width="200px"
          mr={2}
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        <Box ml="auto">
          <Button
            leftIcon={<FontAwesomeIcon icon={faCoins} />}
            onClick={() => fetchCredits(startDate, endDate)}
            isDisabled={!startDate || !endDate}
          >
            Show Usage
          </Button>
        </Box>
        {/* last in the controls, with nothing after it: the shared Download CSV then fetches the
            api's /reports/ai-credits CSV instead of exporting the table on screen */}
        {/* no download while the range is incomplete: the api would default to today */}
        {startDate && endDate && !loadError && <Flex data-report-download-target ml={2} className="no-print" />}
      </Flex>

      {loadError && (
        <Text mb={2} color="red.500" data-testid="ai-credits-error">
          Could not load AI credit usage for this range. Try again.
        </Text>
      )}

      <Box overflowX="auto">
        <Table size="sm" variant="simple" data-testid="ai-credits-table" sx={{ th: { textTransform: "none", letterSpacing: "normal" } }}>
          <Thead>
            <Tr>
              {isMultiLocation && <Th>Location</Th>}
              {CREDIT_COLUMNS.map((column) => (
                <Th key={column.key} isNumeric>{column.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedRows.map((row, index) => (
              <Tr key={String(row.bid || index)} data-testid="ai-credits-row">
                {isMultiLocation && <Td>{row.location_name || "-"}</Td>}
                {CREDIT_COLUMNS.map((column) => (
                  <Td key={column.key} isNumeric fontWeight={column.key === "credits_total" ? "bold" : undefined}>
                    {formatCredits(row[column.key])}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {isMultiLocation && (
            <Tfoot>
              <Tr data-testid="ai-credits-total" fontWeight="bold">
                <Td fontWeight="bold">Total</Td>
                {CREDIT_COLUMNS.map((column) => (
                  <Td key={column.key} isNumeric fontWeight="bold">{formatCredits(totals[column.key])}</Td>
                ))}
              </Tr>
            </Tfoot>
          )}
        </Table>
      </Box>
    </Box>
  );
}

export default AiCredits;
